import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, DatabaseBackup, Download, FileClock,
  HardDrive, HeartPulse, History, Loader2, RefreshCw, RotateCcw, ShieldCheck,
} from 'lucide-react';
import {
  createSystemBackup, downloadSystemBackup, getAdminSystem, restoreSystemBackup, runSystemHealthCheck,
} from '../../services/api';

const money = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₸`;
const dateTime = (value) => value ? new Date(value).toLocaleString('ru-RU') : '—';
const fileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

const eventLabels = {
  closed: 'Сделка закрыта',
  corrected: 'Финансовая корректировка',
  status_changed: 'Статус изменён',
};

const SystemPage = () => {
  const [tab, setTab] = useState('backups');
  const [data, setData] = useState({ backups: [], audits: { data: [] }, system: {} });
  const [auditPage, setAuditPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [notice, setNotice] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [restoreConfirmation, setRestoreConfirmation] = useState('');
  const [restoreError, setRestoreError] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await getAdminSystem(auditPage);
      setData(response);
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || 'Не удалось загрузить данные системы.' });
    } finally {
      setLoading(false);
    }
  }, [auditPage]);

  useEffect(() => { load(); }, [load]);

  const createBackup = async () => {
    setAction('backup');
    setNotice(null);
    try {
      const response = await createSystemBackup();
      setNotice({ type: 'success', text: response.message });
      await load();
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || 'Не удалось создать резервную копию.' });
    } finally {
      setAction(null);
    }
  };

  const checkHealth = async () => {
    setAction('health');
    setNotice(null);
    try {
      const response = await runSystemHealthCheck();
      setNotice({ type: response.healthy ? 'success' : 'error', text: response.message });
      await load();
    } catch (error) {
      setNotice({ type: 'error', text: error.response?.data?.message || 'Проверка системы не выполнена.' });
    } finally {
      setAction(null);
    }
  };

  const openRestoreModal = (backup) => {
    setRestoreTarget(backup);
    setRestorePassword('');
    setRestoreConfirmation('');
    setRestoreError('');
  };

  const closeRestoreModal = () => {
    if (action?.startsWith('restore-')) return;
    setRestoreTarget(null);
  };

  const restoreBackup = async () => {
    if (!restoreTarget || restoreConfirmation !== 'ВОССТАНОВИТЬ' || !restorePassword) return;
    setAction(`restore-${restoreTarget.name}`);
    setNotice(null);
    setRestoreError('');
    try {
      const response = await restoreSystemBackup(restoreTarget.name, restorePassword, restoreConfirmation);
      window.alert(response.message);
      localStorage.clear();
      window.location.href = '/login';
    } catch (error) {
      setRestoreError(error.response?.data?.message || 'Не удалось восстановить резервную копию.');
      setAction(null);
    }
  };

  const backups = data.backups || [];
  const audits = data.audits || { data: [], current_page: 1, last_page: 1 };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 p-3 sm:p-6 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black tracking-tighter text-white flex items-center gap-3 uppercase italic">
              <ShieldCheck className="text-indigo-500" /> Система и аудит
            </h1>
            <p className="text-slate-500 text-xs mt-2">Резервные копии, контроль работоспособности и финансовые изменения</p>
          </div>
          <button onClick={checkHealth} disabled={action !== null} className="px-5 py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-black flex items-center justify-center gap-2 disabled:opacity-50">
            {action === 'health' ? <Loader2 className="animate-spin" size={17} /> : <HeartPulse size={17} />}
            Проверить систему
          </button>
        </div>

        {notice && (
          <div className={`rounded-2xl border p-4 flex items-start gap-3 whitespace-pre-line text-sm ${notice.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300' : 'bg-rose-500/10 border-rose-500/20 text-rose-300'}`}>
            {notice.type === 'success' ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
            <span>{notice.text}</span>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="bg-[#1e293b]/50 border border-white/5 rounded-2xl p-5">
            <HardDrive className="text-indigo-400 mb-3" size={20} />
            <div className="text-2xl font-black text-white">{backups.filter((backup) => backup.valid).length}</div>
            <div className="text-[10px] uppercase font-black text-slate-500 mt-1">Резервных копий</div>
          </div>
          <div className="bg-[#1e293b]/50 border border-white/5 rounded-2xl p-5">
            <RefreshCw className="text-amber-400 mb-3" size={20} />
            <div className="text-2xl font-black text-white">{data.system?.pending_jobs || 0}</div>
            <div className="text-[10px] uppercase font-black text-slate-500 mt-1">В очереди</div>
          </div>
          <div className="bg-[#1e293b]/50 border border-white/5 rounded-2xl p-5">
            <AlertTriangle className="text-rose-400 mb-3" size={20} />
            <div className="text-2xl font-black text-white">{data.system?.failed_jobs || 0}</div>
            <div className="text-[10px] uppercase font-black text-slate-500 mt-1">Ошибок очереди</div>
          </div>
        </div>

        <div className="flex gap-2 border-b border-white/5">
          <button onClick={() => setTab('backups')} className={`px-5 py-3 text-xs font-black border-b-2 ${tab === 'backups' ? 'text-white border-indigo-500' : 'text-slate-500 border-transparent'}`}><DatabaseBackup className="inline mr-2" size={16} />Резервные копии</button>
          <button onClick={() => setTab('audit')} className={`px-5 py-3 text-xs font-black border-b-2 ${tab === 'audit' ? 'text-white border-indigo-500' : 'text-slate-500 border-transparent'}`}><History className="inline mr-2" size={16} />Финансовый аудит</button>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div>
        ) : tab === 'backups' ? (
          <section className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-xs text-slate-500">Срок хранения: {data.system?.retention_days || 30} дней</p>
              <button onClick={createBackup} disabled={action !== null} className="px-5 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black flex items-center justify-center gap-2 disabled:opacity-50">
                {action === 'backup' ? <Loader2 className="animate-spin" size={17} /> : <DatabaseBackup size={17} />}
                Создать копию сейчас
              </button>
            </div>
            <div className="space-y-2">
              {backups.length === 0 ? <div className="p-10 text-center text-slate-500 border border-dashed border-white/10 rounded-2xl">Резервных копий пока нет</div> : backups.map((backup) => (
                <div key={backup.name} className="bg-[#1e293b]/50 border border-white/5 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileClock className={backup.valid ? 'text-indigo-400 shrink-0' : 'text-rose-400 shrink-0'} size={21} />
                    <div className="min-w-0"><div className="font-bold text-white text-sm truncate">{backup.name}</div><div className="text-[11px] text-slate-500 mt-1">{dateTime(backup.created_at)} · {fileSize(backup.size)} {!backup.valid && <span className="text-rose-400 ml-2">Повреждённая копия</span>}</div></div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button onClick={() => downloadSystemBackup(backup.name)} className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-black flex items-center justify-center gap-2"><Download size={15} />Скачать</button>
                    <button onClick={() => openRestoreModal(backup)} disabled={!backup.valid || action !== null} className="px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 hover:bg-amber-500/20 text-xs font-black flex items-center justify-center gap-2 disabled:opacity-30">
                      {action === `restore-${backup.name}` ? <Loader2 className="animate-spin" size={15} /> : <RotateCcw size={15} />}Восстановить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : (
          <section className="space-y-3">
            {audits.data?.length === 0 ? <div className="p-10 text-center text-slate-500 border border-dashed border-white/10 rounded-2xl">Финансовых изменений пока нет</div> : audits.data.map((audit) => (
              <div key={audit.id} className="bg-[#1e293b]/50 border border-white/5 rounded-2xl p-5 grid md:grid-cols-[1.2fr_1fr_1fr] gap-4">
                <div><div className="text-[10px] uppercase font-black text-indigo-400">{eventLabels[audit.event] || audit.event}</div><div className="font-black text-white mt-1">{audit.lead?.client_name || `Сделка #${audit.lead_id}`}</div><div className="text-xs text-slate-500">{audit.lead?.phone || 'Телефон не указан'}</div></div>
                <div><div className="text-[10px] uppercase font-black text-slate-500">Изменение суммы</div><div className="text-sm font-bold mt-1"><span className="text-slate-500">{money(audit.old_amount)}</span> <span className="mx-1">→</span> <span className="text-emerald-400">{money(audit.new_amount)}</span></div><div className="text-xs text-slate-500 mt-1">{audit.old_status || '—'} → {audit.new_status || '—'}</div></div>
                <div><div className="text-sm font-bold text-white">{audit.user?.name || 'Система'}</div><div className="text-xs text-slate-500 mt-1">{dateTime(audit.created_at)}</div>{audit.reason && <div className="text-xs text-amber-300 mt-2">Причина: {audit.reason}</div>}</div>
              </div>
            ))}
            {audits.last_page > 1 && <div className="flex justify-center items-center gap-3 pt-4"><button disabled={audits.current_page <= 1} onClick={() => setAuditPage((p) => p - 1)} className="px-4 py-2 rounded-xl bg-white/5 disabled:opacity-30">Назад</button><span className="text-xs text-slate-500">{audits.current_page} / {audits.last_page}</span><button disabled={audits.current_page >= audits.last_page} onClick={() => setAuditPage((p) => p + 1)} className="px-4 py-2 rounded-xl bg-white/5 disabled:opacity-30">Далее</button></div>}
          </section>
        )}
      </div>

      {restoreTarget && (
        <div className="fixed inset-0 z-[200] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) closeRestoreModal(); }}>
          <div className="w-full max-w-lg bg-[#111c31] border border-rose-500/30 rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-6 sm:p-8 border-b border-white/5">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center mb-5"><AlertTriangle size={25} /></div>
              <h2 className="text-xl sm:text-2xl font-black text-white">Подтверждение восстановления</h2>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">Текущая база данных будет заменена данными из копии <span className="font-bold text-white">{restoreTarget.name}</span>. Перед началом CRM автоматически создаст страховочную копию.</p>
            </div>
            <div className="p-6 sm:p-8 space-y-5">
              {restoreError && <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-sm">{restoreError}</div>}
              <div>
                <label className="block text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Пароль администратора</label>
                <input type="password" autoComplete="current-password" value={restorePassword} onChange={(event) => setRestorePassword(event.target.value)} className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3.5 text-white outline-none focus:border-indigo-500" placeholder="Введите ваш пароль" />
              </div>
              <div>
                <label className="block text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Для подтверждения введите: <span className="text-rose-400">ВОССТАНОВИТЬ</span></label>
                <input value={restoreConfirmation} onChange={(event) => setRestoreConfirmation(event.target.value.toUpperCase())} className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3.5 text-white outline-none focus:border-rose-500" placeholder="ВОССТАНОВИТЬ" />
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                <button onClick={closeRestoreModal} disabled={action?.startsWith('restore-')} className="px-5 py-3 rounded-xl bg-white/5 text-slate-300 font-black text-xs disabled:opacity-40">Отмена</button>
                <button onClick={restoreBackup} disabled={!restorePassword || restoreConfirmation !== 'ВОССТАНОВИТЬ' || action?.startsWith('restore-')} className="px-5 py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-black text-xs flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed">
                  {action?.startsWith('restore-') ? <Loader2 className="animate-spin" size={16} /> : <RotateCcw size={16} />}
                  Восстановить базу
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemPage;
